import { PaymentMethodEnum } from '@/types/common';
import type { AvailablePaymentMethod } from '@/types/checkout';
import type { Template } from '@/types/template';

export interface PresentedPaymentMethodsInput {
  template: Template;
  paymentMethodsAvailable: AvailablePaymentMethod[];
  paymentMethodSelected?: PaymentMethodEnum;
  /**
   * Express wallets that reported themselves painted, from
   * `useExpressComponents`. A configured wallet missing from this set is one
   * the shopper never saw — the PSP said the device can't run it, the SDK
   * failed to load, or the row filtered it out as non-native.
   */
  availableExpressMethods: ReadonlySet<PaymentMethodEnum | string>;
}

/**
 * The payment methods the shopper can actually see and pick, as opposed to the
 * methods the BE offered. Reported as `payment_methods` on CHECKOUT_PRESENTED
 * so wallet load failures are visible in the funnel instead of being counted
 * as impressions.
 *
 * Non-express methods always count, including the ones behind the "Other"
 * dropdown: they render as a real, reachable choice. Express wallets count
 * only once the PSP confirms them, so a DEFAULT screen where Apple Pay never
 * painted reports every method EXCEPT apple.
 *
 * Callers must wait for the express row to settle before reading this —
 * otherwise it reports the mid-load state, where nothing has confirmed yet.
 */
export const getPresentedPaymentMethods = ({
  template,
  paymentMethodsAvailable,
  paymentMethodSelected,
  availableExpressMethods,
}: PresentedPaymentMethodsInput): string[] => {
  // Single-CTA templates (PREFERRED / SINGLE) show neither the picker row nor
  // the express row — exactly one auto-selected method is on screen. An FTD
  // landing on Apple Pay reports ['apple']; if that wallet had failed, the
  // template would already have fallen back to DEFAULT and taken the branch
  // below instead.
  if (!template.features.showPaymentMethods && !template.features.showExpressCheckout) {
    return paymentMethodSelected ? [paymentMethodSelected] : [];
  }

  return paymentMethodsAvailable
    .filter((pm) => pm.paymentMethodName !== PaymentMethodEnum.OTHER)
    .filter((pm) => !pm.isExpress || availableExpressMethods.has(pm.paymentMethodName))
    .map((pm) => pm.paymentMethodName);
};
